import { MUIButton, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { Navigate } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useDownloadFile } from "../../../../../../hooks/useDownloadFile"
import { digitalManagerApi } from "../../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../../components/DiMaDetailsContent"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../../models/Authorization"
import { Guid } from "../../../../../models/CustomType"
import { FileVersionGet, FileVersionPut } from "../../../../../models/FileVersion"
import { UserInfo } from "../../../../../models/User"
import { getNiceFormattedDate } from "../../../../../utils/getFormattedDate"

function formatFileSize(bytes: number) {
  if (bytes === 0) {
    return "0 Bytes"
  }
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"]

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

const validationSchema = z.object({
  suffix: z
    .string()
    .regex(/^[a-zA-Z0-9_-]*$/, {
      message: "Only letters, numbers, underscores (_) and hyphens (-) are allowed",
    })
    .optional(),
})

export function FileVersionDetails(props: {
  readonly version: FileVersionGet
  readonly fileId: Guid
  readonly user: UserInfo
  readonly setVersion: (updatedVersion: FileVersionGet) => void
}) {
  const { version, fileId, user, setVersion } = props

  const returnURL = `/files/${fileId}#versions`
  const { handleDownload, downloading } = useDownloadFile()

  const formMethods = useForm<FileVersionPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      suffix: version.suffix || "",
    })
  }, [version.suffix, reset])

  const onSubmit = useCallback(
    (values: FileVersionPut) =>
      digitalManagerApi
        .put<FileVersionGet>(`/files/${fileId}/versions/${version.id}`, {
          suffix: values.suffix,
        })
        .then((res) => {
          toast.success("Suffix updated")
          setVersion(res.data)
        })
        .catch(() => {
          toast.error("Cannot update suffix")
          throw new Error("Cannot update suffix")
        }),
    [fileId, version.id, setVersion]
  )

  //#region Suffix
  const handleSuffixChange = useCallback(
    (field: ControllerRenderProps<FileVersionPut, "suffix">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderSuffixTextField = useCallback(
    ({ field }: { field: ControllerRenderProps<FileVersionPut, "suffix"> }) => (
      <MUITextField
        id="suffix"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleSuffixChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.suffix?.message === "string"
            ? { suffix: { message: errors.suffix.message } }
            : undefined
        }
      />
    ),
    [errors.suffix, handleSuffixChange]
  )
  //#endregion

  //#region Download
  const getFileName = useCallback(
    () =>
      `${version.file.name}${version.suffix ? version.suffix : ""}.${
        version.file.extension
      }`,
    [version.file.name, version.suffix, version.file.extension]
  )

  const handleDownloadClick = useCallback(() => {
    if (version.downloadLink) {
      handleDownload(version.downloadLink, getFileName())
    }
  }, [handleDownload, version.downloadLink, getFileName])
  //#endregion

  if (!version) {
    return <Navigate to={returnURL} />
  }

  const detailsContentProps: DiMaDetailsContentProps<FileVersionPut> = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToFeature(
      user.authorizations,
      DimaFeatureShortNameEnum.FM,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Suffix",
        displayItem: <div className="title">{version.suffix}</div>,
        editItem: (
          <Controller name="suffix" control={control} render={renderSuffixTextField} />
        ),
      },
      {
        id: 1,
        label: "Size",
        displayItem: <div className="title">{formatFileSize(version.size)}</div>,
      },
      {
        id: 2,
        label: "Upload Date",
        displayItem: (
          <div className="title">{getNiceFormattedDate(version.uploadDate)}</div>
        ),
      },
      {
        id: 3,
        label: "Uploaded By",
        displayItem: <div className="title">{version.uploadedBy.username}</div>,
      },
      {
        id: 4,
        displayItem: (
          <MUIButton
            variant="primary"
            iconName="icon_abb_dias icon-abb_download"
            onClickButton={handleDownloadClick}
            disabled={downloading}
            isLoading={downloading}
            label={downloading ? "Downloading..." : "Download"}
            size={32}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
